<template>
  <div class="dapp clinic-scope" :class="{ 'is-menu-open': menuOpen }">
    <div class="clinic-scrim" @click="menuOpen = false" />

    <!-- Sidebar -->
    <aside class="dside">
      <div class="dside-logo">
        <img
          class="vm-logo"
          src="../assets/images/logo-videomedi-white.png"
          alt="Videomedi"
        />
      </div>

      <!-- Seletor de clínica / unidade -->
      <div class="dside-clinic-wrap">
        <button
          type="button"
          class="dside-clinic"
          @click="unitMenuOpen = !unitMenuOpen"
        >
          <div class="ic">{{ clinicInitials }}</div>
          <div class="stack" style="gap:1px;flex:1;min-width:0">
            <span class="nm">{{ clinicName }}</span>
            <span class="un">{{ unitLabel }}</span>
          </div>
          <svg
            class="chev"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 9l6 6 6-6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
        <ul v-if="unitMenuOpen" class="dside-unit-menu">
          <template v-if="clinics.length > 1">
            <li v-for="c in clinics" :key="`clinic-${c.id}`">
              <button
                type="button"
                data-test="clinic-option"
                :class="{ on: c.id === activeClinicId }"
                @click="onSelectClinic(c.id)"
              >
                {{ c.name }}
                <span>{{ roleLabelOf(c.role) }}</span>
              </button>
            </li>
            <li class="dside-unit-menu__sep" aria-hidden="true" />
          </template>
          <li v-for="u in units" :key="u.id">
            <button
              type="button"
              :class="{ on: u.id === selectedUnitId }"
              @click="onSelectUnit(u.id)"
            >
              {{ u.name }}
              <span>{{ u.doctorCount }} profissionais de saúde</span>
            </button>
          </li>
        </ul>
      </div>

      <nav
        class="dside-nav"
        role="navigation"
        aria-label="Menu da clínica"
        @click="menuOpen = false"
      >
        <template v-for="(section, sIdx) in visibleSections">
          <span class="sb-sec" :key="`sec-${sIdx}`">{{ section.label }}</span>
          <router-link
            v-for="item in section.items"
            :key="item.to"
            class="sb-item"
            :class="{ 'is-active': isActive(item) }"
            :to="item.to"
          >
            <span class="sb-ico" v-html="item.icon" />
            {{ item.label }}
            <span v-if="badgeFor(item)" class="tag">{{ badgeFor(item) }}</span>
          </router-link>
        </template>
      </nav>

      <div class="dside-foot">
        <div class="avatar avatar--sm avatar--accent">
          {{ displayOperator.initials }}
        </div>
        <div class="stack" style="gap:2px;overflow:hidden;flex:1;min-width:0">
          <span class="dside-foot-name">{{ displayOperator.name }}</span>
          <span class="dside-foot-role">{{ roleLabel }}</span>
        </div>
        <button
          type="button"
          class="dside-logout"
          aria-label="Sair"
          title="Sair"
          @click="onLogout"
        >
          Sair
        </button>
      </div>
    </aside>

    <!-- Conteúdo principal -->
    <div class="dmain">
      <!-- Barra mobile com hambúrguer (não existe no protótipo — add p/ produção) -->
      <div class="clinic-mobilebar">
        <button
          type="button"
          class="clinic-hamburger"
          aria-label="Abrir menu"
          @click="menuOpen = true"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 6h16M4 12h16M4 18h16"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
        <img
          class="vm-logo vm-logo--sm"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </div>

      <!-- Trocar de clínica remonta a tela para recarregar os dados dela. -->
      <div :key="activeClinicId || 'none'" class="clinic-view">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
  clinicContext,
  operator as fallbackOperator,
  units as fallbackUnits
} from "@/data/clinica";

// Ícones SVG do handoff (inline p/ herdar currentColor da navegação)
const ICONS = {
  dashboard: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="3" width="7" height="9" rx="1.5" stroke="currentColor" stroke-width="2"/><rect x="14" y="3" width="7" height="5" rx="1.5" stroke="currentColor" stroke-width="2"/><rect x="14" y="12" width="7" height="9" rx="1.5" stroke="currentColor" stroke-width="2"/><rect x="3" y="16" width="7" height="5" rx="1.5" stroke="currentColor" stroke-width="2"/></svg>`,
  patients: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="9" cy="8" r="3.5" stroke="currentColor" stroke-width="2"/><path d="M2 20c0-3.5 3-5.5 7-5.5s7 2 7 5.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M17 9h5M19.5 6.5v5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  followup: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 21s-7-4.5-9.5-9C1 9 2.5 5.5 6 5.5c2 0 3.2 1.2 4 2.3.8-1.1 2-2.3 4-2.3 3.5 0 5 3.5 3.5 6.5C19 16.5 12 21 12 21z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>`,
  agenda: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" stroke-width="2"/><path d="M3 9h18M8 2v4M16 2v4" stroke="currentColor" stroke-width="2"/></svg>`,
  confirm: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 11l3 3L22 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  reschedule: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  finance: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="2"/><path d="M3 10h18" stroke="currentColor" stroke-width="2"/></svg>`,
  doctors: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 3v6a6 6 0 0 0 12 0V3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M6 3H4M6 3h2M18 3h-2M18 3h2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="18" cy="17" r="3" stroke="currentColor" stroke-width="2"/><path d="M12 15v2a3 3 0 0 0 3 3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  config: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`
};

export default {
  name: "ClinicApp",
  data() {
    return {
      menuOpen: false,
      unitMenuOpen: false,
      fallbackOperator,
      clinicContext,
      sections: [
        {
          label: "Principal",
          items: [
            {
              label: "Dashboard",
              to: "/clinica",
              icon: ICONS.dashboard,
              exact: true
            }
          ]
        },
        {
          label: "CRM",
          items: [
            {
              label: "Pacientes",
              to: "/clinica/pacientes",
              icon: ICONS.patients,
              match: "/clinica/pacientes",
              permission: ["pacientes", "view"]
            },
            {
              label: "Acompanhamento",
              to: "/clinica/acompanhamento/sem-retorno",
              icon: ICONS.followup,
              match: "/clinica/acompanhamento",
              badge: "acompanhamento",
              permission: ["acompanhamento", "view"]
            }
          ]
        },
        {
          label: "Operação",
          items: [
            {
              label: "Agenda",
              to: "/clinica/agenda",
              icon: ICONS.agenda,
              match: "/clinica/agenda",
              permission: ["agenda", "view"]
            },
            {
              label: "Confirmações",
              to: "/clinica/confirmacoes",
              icon: ICONS.confirm,
              match: "/clinica/confirmacoes",
              badge: "confirmacoes",
              permission: ["confirmacoes", "view"]
            },
            {
              label: "Reagendamentos",
              to: "/clinica/reagendamentos",
              icon: ICONS.reschedule,
              match: "/clinica/reagendamentos",
              permission: ["reagendamentos", "view"]
            }
          ]
        },
        {
          label: "Gestão",
          items: [
            {
              label: "Financeiro",
              to: "/clinica/financeiro",
              icon: ICONS.finance,
              match: "/clinica/financeiro",
              permission: ["financeiro", "view"]
            },
            {
              label: "Profissionais de saúde",
              to: "/clinica/medicos",
              icon: ICONS.doctors,
              match: "/clinica/medicos",
              permission: ["medicos", "view"]
            },
            {
              label: "Configurações",
              to: "/clinica/config",
              icon: ICONS.config,
              match: "/clinica/config",
              permission: ["config", "view"]
            }
          ]
        }
      ]
    };
  },
  created() {
    this.init();
  },
  mounted() {
    document.body.className = "";
    document.body.classList.add("clinic-app-body");
  },
  methods: {
    ...mapActions("clinica", ["init", "selectUnit", "selectClinic"]),
    ...mapActions("auth", ["logout"]),
    onLogout() {
      this.logout();
    },
    isActive(item) {
      const path = this.$route.path;
      if (item.exact) return path === item.to;
      return path === item.match || path.startsWith(`${item.match}/`);
    },
    badgeFor(item) {
      return item.badge ? this.badges[item.badge] : null;
    },
    roleLabelOf(role) {
      return role === "admin" ? "Administrador" : "Atendimento";
    },
    async onSelectClinic(clinicId) {
      this.unitMenuOpen = false;
      if (clinicId === this.activeClinicId) return;
      await this.selectClinic(clinicId);
      if (this.$route.name !== "ClinicDashboard" && this.$router) {
        this.$router.push({ name: "ClinicDashboard" }).catch(() => {});
      }
    },
    onSelectUnit(unitId) {
      this.selectUnit(unitId);
      this.unitMenuOpen = false;
    }
  },
  computed: {
    ...mapGetters("clinica", [
      "badges",
      "units",
      "selectedUnitId",
      "currentUnit",
      "operator",
      "canAccess",
      "clinics",
      "activeClinic",
      "activeClinicId",
      "isAdmin"
    ]),
    roleLabel() {
      return this.operator || this.activeClinic
        ? this.roleLabelOf(this.isAdmin ? "admin" : "atendimento")
        : this.displayOperator.roleLabel;
    },
    // fallbacks p/ o primeiro render (antes de clinica/init resolver)
    displayOperator() {
      return this.operator || this.fallbackOperator;
    },
    unitList() {
      return this.units && this.units.length ? this.units : fallbackUnits;
    },
    clinicName() {
      return (
        (this.activeClinic && this.activeClinic.name) ||
        this.displayOperator.clinicName ||
        this.clinicContext.name
      );
    },
    clinicInitials() {
      return this.clinicContext.initials;
    },
    unitLabel() {
      const u = this.currentUnit || fallbackUnits[0];
      return u ? `${u.name} · ${u.doctorCount} profissionais de saúde` : "";
    },
    visibleSections() {
      return this.sections
        .map(s => ({
          ...s,
          items: s.items.filter(i => this.canAccess(i.permission))
        }))
        .filter(s => s.items.length);
    }
  },
  watch: {
    // Fecha menus ao trocar de rota
    $route() {
      this.menuOpen = false;
      this.unitMenuOpen = false;
    }
  }
};
</script>

<style lang="scss">
.vm-logo--sm {
  height: 22px;
}

.vm-logo {
  display: block;
  height: 28px;
  width: auto;
}

.clinic-app-body {
  background: var(--color-bg-page);
}

.sb-ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

.dside-clinic-wrap {
  position: relative;
}
.dside-unit-menu {
  list-style: none;
  margin: 0 0 var(--space-2);
  padding: var(--space-1);
  background: var(--color-bg-sidebar-item);
  border-radius: var(--radius-md);

  button {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
    width: 100%;
    background: transparent;
    border: none;
    text-align: left;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    color: var(--color-gray-300);
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    cursor: pointer;

    span {
      font-size: var(--font-size-xs);
      color: var(--color-gray-500);
      font-weight: var(--fw-regular);
    }
    &:hover {
      background: rgba(255, 255, 255, 0.06);
      color: #fff;
    }
    &.on {
      color: #fff;
    }
    &.on span {
      color: var(--color-gray-400);
    }
  }
}
.dside-unit-menu__sep {
  height: 1px;
  margin: var(--space-1) var(--space-2);
  background: rgba(255, 255, 255, 0.08);
}

.dside-foot-name {
  color: #fff;
  font-size: var(--font-size-sm);
  font-weight: var(--fw-semibold);
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dside-foot-role {
  font-size: 10px;
  font-weight: var(--fw-bold);
  color: var(--color-gray-400);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.dside-logout {
  border: none;
  background: transparent;
  color: var(--color-gray-400);
  font-size: 11px;
  font-weight: var(--fw-bold);
  padding: 4px 8px;
  border-radius: var(--radius-md);
  cursor: pointer;

  &:hover {
    color: var(--color-accent);
    background: var(--color-bg-sidebar-item);
  }
}

.clinic-mobilebar {
  display: none;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background: #fff;
  border-bottom: 1px solid var(--color-gray-200);
  position: sticky;
  top: 0;
  z-index: 30;
}

@media (max-width: 860px) {
  .clinic-mobilebar {
    display: flex;
  }
}
</style>
